
import React, { useEffect, useState } from 'react';
import { getAllDeviceConfigs, getDeviceLogs } from '../services/api';
import { Calendar, Search, Server, Clock, Activity, AlertCircle } from 'lucide-react';

const DeviceLogs = () => {
    const [deviceList, setDeviceList] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedIp, setSelectedIp] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [error, setError] = useState(null);

    // Initial Fetch (Devices)
    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const response = await getAllDeviceConfigs(1, 500);
                const devices = response?.data || [];
                setDeviceList(devices);
                if (devices.length > 0) {
                    setSelectedIp(devices[0].ip);
                }
            } catch (err) {
                console.error("Failed to fetch devices:", err);
                setError("Failed to load device list.");
            } finally {
                setInitialLoading(false);
            }
        };
        fetchDevices();
    }, []);

    // Initial Fetch (Logs) when dependencies change
    useEffect(() => {
        if (selectedIp && selectedDate) {
            fetchLogs();
        }
    }, [selectedIp, selectedDate]);

    const fetchLogs = async () => {
        if (!selectedIp || !selectedDate) return;
        setLoading(true);
        setError(null);
        try {
            const response = await getDeviceLogs(selectedIp, selectedDate);
            setLogs(response?.data || []);
        } catch (err) {
            console.error("Failed to fetch logs:", err);
            setError("Failed to fetch logs. Please try again.");
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return <div className="p-8 flex items-center justify-center text-gray-500">Loading resources...</div>;
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">
            {/* Header Section */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Device Logs</h1>
                <p className="text-sm text-gray-500 mt-1">Monitor connectivity history and response times.</p>
            </div>

            {/* Filter / Control Bar */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-4 items-end sm:items-center">
                
                {/* Device Selector */}
                <div className="flex-1 w-full">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Selected Device</label>
                    <div className="relative group">
                        <Server className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        <select
                            value={selectedIp}
                            onChange={(e) => setSelectedIp(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                        >
                            <option value="" disabled>Select Device</option>
                            {deviceList?.map((device) => (
                                <option key={device.ip} value={device.ip}>
                                    {device.deviceName} ({device.ip})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Date Selector */}
                <div className="w-full sm:w-64">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Date Range</label>
                    <div className="relative group">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all"
                        />
                    </div>
                </div>

                {/* Refresh Button */}
                <button
                    onClick={fetchLogs}
                    disabled={loading}
                    className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Search className="h-4 w-4" />}
                    <span>Fetch Data</span>
                </button>
            </div>

            {/* Error State */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-lg flex items-center gap-3 text-red-700 text-sm">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    {error}
                </div>
            )}

            {/* Main Table Card */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                <th className="px-6 py-4 w-24">Status</th>
                                <th className="px-6 py-4">Timestamp</th>
                                <th className="px-6 py-4">Response Latency</th>
                                <th className="px-6 py-4 text-right">Device IP</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {!loading && logs.length > 0 ? (
                                logs.map((log) => (
                                    <tr key={log.id} className="group hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-3.5">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
                                                log.status === 'Online'
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                    : 'bg-rose-50 text-rose-700 border-rose-200'
                                            }`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${log.status === 'Online' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                {log.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <Clock className="h-3.5 w-3.5 text-gray-400" />
                                                <span className="font-medium">
                                                    {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                </span>
                                                <span className="text-gray-400 text-xs ml-1">
                                                    {new Date(log.timestamp).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <Activity className={`h-4 w-4 ${log.responseTime > 500 ? 'text-amber-500' : 'text-gray-400'}`} />
                                                <span className={`font-mono text-sm font-medium ${
                                                    log.responseTime > 500 ? 'text-amber-600' : 'text-gray-600'
                                                }`}>
                                                    {log.responseTime} <span className="text-xs text-gray-400">ms</span>
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3.5 text-right">
                                            <span className="font-mono text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                {log.deviceIp}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="px-6 py-16 text-center text-gray-500">
                                        {!loading ? (
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <div className="p-3 bg-gray-50 rounded-full">
                                                    <Server className="h-6 w-6 text-gray-300" />
                                                </div>
                                                <span className="text-sm font-medium">No activity logs found</span>
                                                <span className="text-xs text-gray-400">Try selecting a different date or device.</span>
                                            </div>
                                        ) : (
                                            <div className="min-h-[200px] flex items-center justify-center text-sm text-gray-400">Fetching logs...</div>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DeviceLogs;

